import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  format, differenceInHours, eachDayOfInterval, eachHourOfInterval,
} from "date-fns";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  ArrowDownToLine, ArrowUpFromLine, Building2, TrendingUp,
  CheckCircle2, Activity, Wallet, RefreshCw, Server, Zap,
  BarChart3, ArrowUpRight, Globe,
} from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import DateFilter from "@/shared/components/dashboard/DateFilter";
import { getDateRange, filterTransactionsByDateRange } from "@/shared/utils/dateFilters";

/* ── Stat Card ── */
function StatCard({ title, value, icon: Icon, accent, delay = 0, trend }) {
  const [hov, setHov] = React.useState(false);
  const [gp, setGp] = React.useState({ x: 50, y: 50 });
  const ref = React.useRef(null);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 26, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ delay, type: "spring", stiffness: 200, damping: 22 }}
      onHoverStart={() => setHov(true)}
      onHoverEnd={() => setHov(false)}
      onMouseMove={(e) => {
        const r = ref.current?.getBoundingClientRect();
        if (r) setGp({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
      }}
      whileHover={{ y: -5, scale: 1.025, transition: { type: "spring", stiffness: 280, damping: 22 } }}
      style={{
        position: "relative", overflow: "hidden", borderRadius: "18px", padding: "20px",
        cursor: "default", fontFamily: "'Plus Jakarta Sans', sans-serif",
        background: "linear-gradient(150deg, rgba(8,16,34,0.98) 0%, rgba(11,22,44,0.96) 100%)",
        border: hov ? `1px solid ${accent}42` : "1px solid rgba(255,255,255,0.054)",
        boxShadow: hov ? `0 16px 48px -8px ${accent}28, 0 0 0 1px ${accent}16` : "0 3px 18px rgba(0,0,0,0.38)",
        transition: "border 0.35s ease, box-shadow 0.35s ease",
      }}
    >
      {/* Soft radial cursor glow */}
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: `radial-gradient(circle at ${gp.x}% ${gp.y}%, ${accent}16 0%, transparent 62%)`,
        opacity: hov ? 1 : 0, transition: "opacity 0.3s ease",
      }} />
      <div style={{ position: "absolute", top: 0, right: 0, width: "70px", height: "70px", background: `radial-gradient(circle at 100% 0%, ${accent}10 0%, transparent 70%)`, transform: "translate(20%,-20%)", pointerEvents: "none" }} />

      {/* Accent bottom line */}
      <motion.div
        style={{ position: "absolute", bottom: 0, left: 0, height: "2px", background: `linear-gradient(90deg, ${accent}cc, ${accent}55, transparent)` }}
        animate={{ width: hov ? "100%" : "0%", opacity: hov ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.34, 1.56, 0.64, 1] }}
      />

      <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: `${accent}90`, marginBottom: "10px" }}>{title}</p>
          <motion.p
            style={{ fontSize: "22px", fontWeight: 900, color: "#f0f9ff", letterSpacing: "-0.03em", margin: 0 }}
            animate={{ scale: hov ? 1.04 : 1 }}
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
          >
            {value}
          </motion.p>
          {trend && (
            <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "8px" }}>
              <TrendingUp style={{ width: "11px", height: "11px", color: accent }} />
              <span style={{ fontSize: "11px", fontWeight: 600, color: accent, opacity: hov ? 1 : 0.5, transition: "opacity 0.25s" }}>{trend}</span>
            </div>
          )}
        </div>

        <motion.div
          animate={{ rotate: hov ? [0, -8, 5, -2, 0] : 0, scale: hov ? 1.15 : 1, y: hov ? -2 : 0 }}
          transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <div style={{
            width: "44px", height: "44px", borderRadius: "13px",
            background: `${accent}14`, border: `1px solid ${accent}30`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: hov ? `0 8px 22px ${accent}22` : "none",
            transition: "box-shadow 0.3s ease",
          }}>
            <Icon style={{ width: "18px", height: "18px", color: accent }} />
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

/* ── Quick Action ── */
function QuickAction({ to, icon: Icon, label, accent, delay = 0 }) {
  const [hov, setHov] = React.useState(false);
  return (
    <Link to={to} style={{ textDecoration: "none" }}>
      <motion.div
        initial={{ opacity: 0, y: 18, scale: 0.93 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay, type: "spring", stiffness: 200, damping: 20 }}
        onHoverStart={() => setHov(true)}
        onHoverEnd={() => setHov(false)}
        whileHover={{ y: -6, scale: 1.03, transition: { type: "spring", stiffness: 300, damping: 22 } }}
        whileTap={{ scale: 0.96 }}
        style={{
          position: "relative", overflow: "hidden",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px",
          padding: "22px 14px", borderRadius: "18px", cursor: "pointer",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          background: hov
            ? "linear-gradient(150deg, rgba(10,22,44,1) 0%, rgba(13,26,52,1) 100%)"
            : "linear-gradient(150deg, rgba(8,16,34,0.96) 0%, rgba(11,22,44,0.94) 100%)",
          border: hov ? `1px solid ${accent}3e` : "1px solid rgba(255,255,255,0.05)",
          boxShadow: hov ? `0 20px 52px -8px ${accent}28, 0 0 0 1px ${accent}14` : "0 3px 16px rgba(0,0,0,0.32)",
          transition: "border 0.32s ease, box-shadow 0.32s ease, background 0.32s ease",
        }}
      >
        <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 50% 65%, ${accent}10 0%, transparent 65%)`, opacity: hov ? 1 : 0, transition: "opacity 0.3s ease", pointerEvents: "none" }} />

        <motion.div
          animate={{ rotate: hov ? [0, -8, 6, -2, 0] : 0, scale: hov ? 1.14 : 1, y: hov ? -3 : 0 }}
          transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
        >
          <div style={{
            width: "52px", height: "52px", borderRadius: "16px",
            background: `${accent}14`, border: `1px solid ${accent}2e`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: hov ? `0 10px 28px ${accent}20` : "none",
            transition: "box-shadow 0.3s ease",
          }}>
            <Icon style={{ width: "22px", height: "22px", color: accent }} />
          </div>
        </motion.div>

        <span style={{ fontSize: "13px", fontWeight: 700, color: hov ? "#e2e8f0" : "#64748b", transition: "color 0.22s ease", position: "relative", zIndex: 1 }}>{label}</span>

        <motion.div
          style={{ position: "absolute", bottom: 0, left: 0, height: "2px", background: `linear-gradient(90deg, transparent, ${accent}bb, transparent)` }}
          animate={{ width: hov ? "100%" : "0%", opacity: hov ? 1 : 0 }}
          transition={{ duration: 0.42, ease: [0.34, 1.56, 0.64, 1] }}
        />
      </motion.div>
    </Link>
  );
}

/* ── Tooltip ── */
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ borderRadius: "16px", padding: "14px 18px", background: "rgba(6,12,24,0.97)", border: "1px solid rgba(6,182,212,0.22)", boxShadow: "0 16px 48px rgba(0,0,0,0.6)", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <p style={{ fontSize: "10px", color: "#475569", marginBottom: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em" }}>{label}</p>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", marginBottom: "4px" }}>
          <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: p.color }} />
          <span style={{ color: "#64748b", textTransform: "capitalize", fontWeight: 500 }}>{p.dataKey}:</span>
          <span style={{ color: "#fff", fontWeight: 800 }}>฿{(p.value * 1000).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
};

export default function ResellerDashboard() {
  const { t } = useLanguage();
  const [dateFilter, setDateFilter] = React.useState("today");
  const [customDateRange, setCustomDateRange] = React.useState({ from: null, to: null });
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const resellerName = "Asia Gaming Network";

  const { data: merchants = [], refetch } = useQuery({ queryKey: ["reseller-merchants"], queryFn: async () => [] });
  const { data: transactions = [] } = useQuery({ queryKey: ["reseller-transactions"], queryFn: async () => [] });

  const dateRange = React.useMemo(() => getDateRange(dateFilter, customDateRange), [dateFilter, customDateRange]);
  const filteredTransactions = React.useMemo(
    () => filterTransactionsByDateRange(transactions, dateRange.startDate, dateRange.endDate),
    [transactions, dateRange]
  );

  const totalVolume = filteredTransactions.reduce((s, t) => s + (t.amount || 0), 0);
  const totalDeposits = filteredTransactions.filter(t => t.type === "deposit" && t.status === "success").reduce((s, t) => s + (t.amount || 0), 0);
  const totalWithdrawals = filteredTransactions.filter(t => t.type === "withdrawal" && t.status === "success").reduce((s, t) => s + (t.amount || 0), 0);
  const activeMerchants = merchants.filter(m => m.status === "active").length;
  const successRate = filteredTransactions.length > 0
    ? ((filteredTransactions.filter(t => t.status === "success").length / filteredTransactions.length) * 100).toFixed(1)
    : "100.0";

  const chartData = React.useMemo(() => {
    const { startDate, endDate } = dateRange;
    const hoursDiff = differenceInHours(endDate, startDate);
    if (hoursDiff <= 48) {
      return eachHourOfInterval({ start: startDate, end: endDate }).map((hour) => {
        const txs = filteredTransactions.filter(t => { const d = new Date(t.created_date); return d >= hour && d < new Date(hour.getTime() + 3600000) && t.status === "success"; });
        return { date: format(hour, "HH:mm"), deposits: txs.filter(t => t.type === "deposit").reduce((s, t) => s + (t.amount || 0), 0) / 1000, withdrawals: txs.filter(t => t.type === "withdrawal").reduce((s, t) => s + (t.amount || 0), 0) / 1000 };
      });
    }
    return eachDayOfInterval({ start: startDate, end: endDate }).map((day) => {
      const txs = filteredTransactions.filter(t => { const d = new Date(t.created_date); return d >= day && d < new Date(day.getTime() + 86400000) && t.status === "success"; });
      return { date: format(day, "MMM d"), deposits: txs.filter(t => t.type === "deposit").reduce((s, t) => s + (t.amount || 0), 0) / 1000, withdrawals: txs.filter(t => t.type === "withdrawal").reduce((s, t) => s + (t.amount || 0), 0) / 1000 };
    });
  }, [filteredTransactions, dateRange]);

  const recentTransactions = filteredTransactions.slice(0, 8);
  const stats = [
    { title: t("totalVolume"), value: `฿${totalVolume.toLocaleString()}`, icon: Wallet, accent: "#06b6d4", delay: 0, trend: "+12.4%" },
    { title: t("deposits"), value: `฿${totalDeposits.toLocaleString()}`, icon: ArrowDownToLine, accent: "#818cf8", delay: 0.07, trend: "+8.2%" },
    { title: t("withdrawals"), value: `฿${totalWithdrawals.toLocaleString()}`, icon: ArrowUpFromLine, accent: "#34d399", delay: 0.14, trend: "+5.1%" },
    { title: t("successRate"), value: `${successRate}%`, icon: CheckCircle2, accent: "#22d3ee", delay: 0.21 },
    { title: t("merchants"), value: activeMerchants || 0, icon: Building2, accent: "#f59e0b", delay: 0.28 },
  ];

  const PANEL = { background: "linear-gradient(150deg, rgba(8,16,34,0.97) 0%, rgba(11,22,44,0.95) 100%)", border: "1px solid rgba(255,255,255,0.052)", borderRadius: "20px" };

  return (
    <ResellerLayout currentPage="ResellerDashboard" resellerName={resellerName}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');
        .dash-page { font-family: 'Plus Jakarta Sans', sans-serif; }
        .stat-grid { display: grid; grid-template-columns: repeat(2,1fr); gap: 12px; }
        .chart-grid { display: grid; grid-template-columns: 1fr; gap: 16px; }
        .qa-grid   { display: grid; grid-template-columns: repeat(2,1fr); gap: 10px; }
        @media(min-width:640px)  { .stat-grid { grid-template-columns: repeat(3,1fr); } .qa-grid { grid-template-columns: repeat(4,1fr); } }
        @media(min-width:1024px) { .stat-grid { grid-template-columns: repeat(5,1fr); } .chart-grid { grid-template-columns: 2fr 1fr; } }
        .slim-scroll::-webkit-scrollbar { width: 3px; }
        .slim-scroll::-webkit-scrollbar-track { background: transparent; }
        .slim-scroll::-webkit-scrollbar-thumb { background: rgba(6,182,212,0.2); border-radius: 99px; }
        @keyframes live-dot { 0%,100%{ box-shadow:0 0 0 0 rgba(52,211,153,0.55); } 50%{ box-shadow:0 0 0 5px rgba(52,211,153,0); } }
      `}</style>

      <div className="dash-page" style={{ display: "flex", flexDirection: "column", gap: "18px" }}>

        {/* HEADER */}
        <motion.div 
          initial={{ opacity: 0, y: -16 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.48, ease: [0.22, 1, 0.36, 1] }}
          style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "12px" }}
        >
          <div>
            {/* Live indicator */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "5px" }}>
              <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#34d399", animation: "live-dot 2.2s ease-in-out infinite" }} />
              <span style={{ fontSize: "10px", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 700, color: "rgba(6,182,212,0.78)" }}>Live</span>
              <span style={{ fontSize: "11px", color: "#64748b", fontWeight: 600 }}>{format(new Date(), "EEE, MMM d")}</span>
            </div>

            {/* Reseller Dashboard Title */}
            <h1 style={{ fontSize: "30px", fontWeight: 900, color: "#f0f9ff", letterSpacing: "-0.04em", margin: 0 }}>
              Reseller <span style={{ background: "linear-gradient(135deg, #38bdf8 0%, #22d3ee 40%, #67e8f9 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", display: "inline" }}>Dashboard</span>
            </h1>
        
            <p style={{ color: "#475569", fontSize: "13px", margin: "2px 0 0", fontWeight: 500 }}>
              {t("welcomeBack")}, <span style={{ color: "#06b6d4", fontWeight: 700 }}>{resellerName}</span>
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <DateFilter value={dateFilter} onChange={setDateFilter} customDateRange={customDateRange} onCustomDateRangeChange={setCustomDateRange} />
            <motion.button 
              onClick={async () => { setIsRefreshing(true); await refetch(); setTimeout(() => setIsRefreshing(false), 700); }}
              whileHover={{ scale: 1.07 }} 
              whileTap={{ scale: 0.93 }}
              style={{ width: "40px", height: "40px", borderRadius: "12px", background: "rgba(6,182,212,0.08)", border: "1px solid rgba(6,182,212,0.2)", color: "#06b6d4", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", outline: "none" }}
            >
              <motion.div animate={{ rotate: isRefreshing ? 360 : 0 }} transition={{ duration: 0.75, ease: "linear", repeat: isRefreshing ? Infinity : 0 }}>
                <RefreshCw style={{ width: "15px", height: "15px" }} />
              </motion.div>
            </motion.button>
          </div>
        </motion.div>

        {/* STATS */}
        <div className="stat-grid">
          {stats.map((s) => <StatCard key={s.title} {...s} />)}
        </div>

        {/* CHART + RECENT */}
        <div className="chart-grid">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32, type: "spring", stiffness: 170, damping: 20 }}
            style={{ ...PANEL, padding: "22px 24px", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, right: 0, width: "180px", height: "140px", background: "radial-gradient(circle at 100% 0%, rgba(6,182,212,0.05) 0%, transparent 70%)", pointerEvents: "none" }} />
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: "10px", marginBottom: "20px" }}>
              <div>
                <h3 style={{ fontSize: "15px", fontWeight: 800, color: "#f0f9ff", margin: 0, letterSpacing: "-0.02em" }}>{t("volumeOverviewTitle")}</h3>
                <p style={{ fontSize: "11px", color: "#334155", margin: "2px 0 0" }}>{t("transactionVolume")}</p>
              </div>
              <div style={{ display: "flex", gap: "14px" }}>
                {[{ c: "#06b6d4", l: t("deposits") }, { c: "#34d399", l: t("withdrawals") }].map(({ c, l }) => (
                  <div key={l} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <div style={{ width: "18px", height: "3px", borderRadius: "99px", background: c }} />
                    <span style={{ fontSize: "11px", color: "#475569", fontWeight: 600 }}>{l}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ height: "220px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gD2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3} /><stop offset="95%" stopColor="#06b6d4" stopOpacity={0} /></linearGradient>
                    <linearGradient id="gW2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#34d399" stopOpacity={0.3} /><stop offset="95%" stopColor="#34d399" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.035)" />
                  <XAxis dataKey="date" stroke="#1e2d40" fontSize={10} tick={{ fill: "#334155" }} />
                  <YAxis stroke="#1e2d40" fontSize={10} tick={{ fill: "#334155" }} tickFormatter={(v) => `${v}K`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="deposits" stroke="#06b6d4" strokeWidth={2.5} fill="url(#gD2)" dot={false} activeDot={{ r: 4, fill: "#06b6d4", stroke: "#060c18", strokeWidth: 2 }} />
                  <Area type="monotone" dataKey="withdrawals" stroke="#34d399" strokeWidth={2.5} fill="url(#gW2)" dot={false} activeDot={{ r: 4, fill: "#34d399", stroke: "#060c18", strokeWidth: 2 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, type: "spring", stiffness: 170, damping: 20 }}
            style={{ ...PANEL, padding: "22px", display: "flex", flexDirection: "column", overflow: "hidden", position: "relative" }}>
            <div style={{ position: "absolute", bottom: 0, left: 0, width: "120px", height: "100px", background: "radial-gradient(circle at 0% 100%, rgba(129,140,248,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
                <div style={{ width: "3px", height: "18px", borderRadius: "99px", background: "linear-gradient(180deg,#818cf8,#6366f1)" }} />
                <h3 style={{ fontSize: "14px", fontWeight: 800, color: "#f0f9ff", margin: 0 }}>{t("recent")}</h3>
              </div>
              <Link to={createPageUrl("ResellerTransactions")} style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "11px", fontWeight: 700, color: "#06b6d4", textDecoration: "none" }}>
                {t("viewAll")} <ArrowUpRight style={{ width: "12px", height: "12px" }} />
              </Link>
            </div>
            <div className="slim-scroll" style={{ flex: 1, overflowY: "auto" }}>
              {recentTransactions.length === 0 ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "36px 0", gap: "10px" }}>
                  <motion.div animate={{ y: [0, -5, 0] }} transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
                    style={{ width: "44px", height: "44px", borderRadius: "14px", background: "rgba(99,102,241,0.07)", border: "1px solid rgba(99,102,241,0.13)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <BarChart3 style={{ width: "18px", height: "18px", color: "#334155" }} />
                  </motion.div>
                  <p style={{ fontSize: "12px", color: "#334155", fontWeight: 500, margin: 0 }}>{t("noTransactionsYet")}</p>
                </div>
              ) : recentTransactions.map((tx, i) => {
                const isD = tx.type === "deposit";
                const acc = isD ? "#06b6d4" : "#34d399";
                return (
                  <motion.div key={tx.id || i}
                    initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.04 * i, type: "spring", stiffness: 200, damping: 22 }}
                    whileHover={{ x: 4, backgroundColor: `${acc}07` }}
                    style={{ display: "flex", alignItems: "center", gap: "10px", padding: "9px 8px", borderRadius: "12px", cursor: "pointer", transition: "background 0.2s ease" }}>
                    <motion.div whileHover={{ rotate: 10, scale: 1.13 }} transition={{ type: "spring", stiffness: 380 }}
                      style={{ width: "34px", height: "34px", borderRadius: "10px", background: `${acc}12`, border: `1px solid ${acc}26`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      {isD ? <ArrowDownToLine style={{ width: "13px", height: "13px", color: acc }} /> : <ArrowUpFromLine style={{ width: "13px", height: "13px", color: acc }} />}
                    </motion.div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: "12px", fontWeight: 700, color: "#e2e8f0", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{tx.merchant_code || "N/A"}</p>
                      <p style={{ fontSize: "10px", color: "#334155", margin: 0 }}>{tx.created_date ? format(new Date(tx.created_date), "HH:mm") : "—"}</p>
                    </div>
                    <p style={{ fontSize: "12px", fontWeight: 800, color: acc, margin: 0 }}>฿{tx.amount?.toLocaleString()}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* QUICK ACTIONS */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.48, type: "spring", stiffness: 170, damping: 20 }}
          style={{ ...PANEL, padding: "22px 24px", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 0%, rgba(6,182,212,0.03) 0%, transparent 65%)", pointerEvents: "none" }} />
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px", position: "relative", zIndex: 1 }}>
            <Zap style={{ width: "14px", height: "14px", color: "#06b6d4" }} />
            <h3 style={{ fontSize: "14px", fontWeight: 800, color: "#f0f9ff", margin: 0 }}>{t("quickActions")}</h3>
            <div style={{ flex: 1, height: "1px", background: "linear-gradient(90deg, rgba(6,182,212,0.18), transparent)" }} />
          </div>
          <div className="qa-grid" style={{ position: "relative", zIndex: 1 }}>
            <QuickAction to={createPageUrl("ResellerMerchants")} icon={Building2} label={t("merchants")} accent="#f59e0b" delay={0.5} />
            <QuickAction to={createPageUrl("ResellerTransactions")} icon={Activity} label={t("transactions")} accent="#818cf8" delay={0.56} />
            <QuickAction to={createPageUrl("ResellerRouting")} icon={Globe} label={t("routing")} accent="#34d399" delay={0.62} />
            <QuickAction to={createPageUrl("ResellerProviders")} icon={Server} label={t("providers")} accent="#06b6d4" delay={0.68} />
          </div>
        </motion.div>

      </div>
    </ResellerLayout>
  );
}